/**
 * Built-in locations (Andhra Pradesh), from the government workbooks the owner
 * gave (owner, 2026-10-04: "we have fixed things the user can opt from"). The
 * admin presses Location in the editor; the executive picks each level from a
 * dropdown narrowed by the level above (District → Mandal → Village…).
 *
 * The data itself is static files under apps/web/public/locations (built by
 * scripts/build-locations.mjs), loaded one district at a time.
 */
import { z } from 'zod';

export const LOCATION_SETS = ['village', 'panchayat', 'town'] as const;
export const LocationSet = z.enum(LOCATION_SETS);
export type LocationSet = z.infer<typeof LocationSet>;

export interface LocationSetInfo {
  label: string;
  /** Level names, top first; level 0 is always the District. */
  levels: readonly string[];
  /** Levels offered when the admin inserts a location (deeper ones can be ticked on). */
  defaultDepth: number;
}

export const LOCATION_SET_INFO: Record<LocationSet, LocationSetInfo> = {
  village: { label: 'Village (Mandal and Revenue Village)', levels: ['District', 'Mandal', 'Revenue Village', 'Habitation'], defaultDepth: 3 },
  panchayat: { label: 'Panchayat', levels: ['District', 'Mandal', 'Panchayat', 'Habitation'], defaultDepth: 3 },
  town: { label: 'Town (Municipality and Ward)', levels: ['District', 'Municipality', 'Ward'], defaultDepth: 3 },
};

/** How a chosen place prints. */
export const LOCATION_PRINT = ['en', 'te', 'both'] as const;
export const LocationPrint = z.enum(LOCATION_PRINT);
export type LocationPrint = z.infer<typeof LocationPrint>;
export const LOCATION_PRINT_LABEL: Record<LocationPrint, string> = { en: 'English', te: 'Telugu', both: 'English (Telugu)' };

export const LocationRef = z.strictObject({
  set: LocationSet,
  level: z.int().min(0).max(3),
  /** Fields of one address share a group: a Mandal is narrowed by the District of its own group. */
  group: z.string().regex(/^[a-z0-9_]{1,24}$/),
  print: LocationPrint.optional(),
});
export type LocationRef = z.infer<typeof LocationRef>;

/**
 * A chosen place is stored as "ENGLISH NAME" or "ENGLISH NAME<TAB>తెలుగు పేరు":
 * the English name finds the next level; the Telugu name lets it print in Telugu
 * without loading the data again (the Worker never loads it).
 */
export const LOCATION_SEP = '\t';

export function locationValue(en: string, te: string): string {
  return te ? `${en}${LOCATION_SEP}${te}` : en;
}

export function locationParts(value: string): { en: string; te: string } {
  const i = value.indexOf(LOCATION_SEP);
  return i < 0 ? { en: value.trim(), te: '' } : { en: value.slice(0, i).trim(), te: value.slice(i + 1).trim() };
}

export function formatLocation(value: string, print: LocationPrint = 'en'): string {
  const { en, te } = locationParts(value);
  if (print === 'te') return te || en;
  if (print === 'both') return te ? `${en} (${te})` : en;
  return en;
}
